import React from 'react';
import { AlertTriangle } from 'lucide-react';

// Global error boundary — catches any uncaught render error so the whole app
// never goes blank. Especially important after a failed validation call where
// rendering a raw error object would otherwise crash the React tree.
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Best-effort log; never throw from here.
    try { console.error('App error boundary caught:', error, info); } catch (_) { /* noop */ }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const message = (this.state.error && (this.state.error.message || String(this.state.error))) || 'An unexpected error occurred.';

    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-[#050505] text-[#D4D4D8]" data-testid="app-error-boundary">
        <div className="max-w-md w-full bg-gradient-to-br from-[#1A0610] via-[#0F1115] to-[#0A0A0A] border border-[#E11D48]/30 rounded-3xl p-6 relative overflow-hidden">
          <div className="absolute -top-16 -right-16 w-40 h-40 bg-[#E11D48]/20 blur-3xl rounded-full pointer-events-none" />

          <div className="relative">
            <div className="w-12 h-12 rounded-2xl bg-[#E11D48]/15 border border-[#E11D48]/30 flex items-center justify-center mb-4">
              <AlertTriangle className="w-6 h-6 text-[#E11D48]" />
            </div>

            <h2 className="font-['Unbounded'] text-lg text-white mb-1.5">Something glitched</h2>
            <p className="text-sm text-[#A1A1AA] leading-relaxed mb-4">
              Don't worry — <span className="text-white font-medium">your draft is safe</span>. Any character you were working on is saved to your account under <span className="font-bold text-white">Profile → Drafts</span>.
            </p>

            <details className="text-[11px] text-[#71717A] mb-5 cursor-pointer">
              <summary className="hover:text-[#A1A1AA]">Show technical details</summary>
              <pre className="mt-2 p-2 bg-black/30 rounded-lg text-[10px] text-[#71717A] overflow-x-auto max-h-32 whitespace-pre-wrap break-all">{message}</pre>
            </details>

            <div className="grid grid-cols-2 gap-2">
              <a
                href="/profile"
                className="text-center py-2.5 rounded-full bg-white/5 hover:bg-white/10 text-sm font-medium text-white transition-colors"
              >
                Go to Drafts
              </a>
              <button
                onClick={() => { this.handleReset(); window.location.reload(); }}
                className="py-2.5 rounded-full bg-gradient-to-r from-[#FECDD3] via-[#F472B6] to-[#E11D48] text-[#2A0314] font-bold text-sm hover:scale-[1.02] transition-transform"
                data-testid="error-boundary-reload-btn"
              >
                Reload App
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
